import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS, firestore } from '../firebase';

export type VibeConnectionResolution = {
  status: 'private_chat' | 'accepted_vibe' | 'legacy_match' | 'none';
  chatId?: string;
  chatKind?: 'vibe' | 'match';
  requestId?: string;
  legacyMatchId?: string;
};

export type VibeAlignmentLabel = 'recommended_vibe' | 'strong_sync' | 'instant_sync';

export function getVibeAlignmentLabel(similarityPercent: number): VibeAlignmentLabel | null {
  if (similarityPercent >= 100) return 'instant_sync';
  if (similarityPercent >= 90) return 'strong_sync';
  if (similarityPercent >= 80) return 'recommended_vibe';
  return null;
}

export function getVibeActionLabel(
  label: VibeAlignmentLabel,
  connection?: VibeConnectionResolution,
) {
  if (connection?.chatId) return label === 'instant_sync' ? 'Open chat' : 'Continue chat';
  if (connection?.status === 'accepted_vibe') return 'Open chat';
  if (label === 'recommended_vibe') return 'Send vibe';
  if (label === 'strong_sync') return 'Start sync';
  return 'Open chat';
}

function pairChatId(leftUserId: string, rightUserId: string) {
  return [leftUserId, rightUserId].sort().join('_');
}

function pairMarkerId(dateKey: string, leftUserId: string, rightUserId: string) {
  return `${dateKey}_${[leftUserId, rightUserId].sort().join('_')}`;
}

function hasBothUsers(data: { users?: unknown }, leftUserId: string, rightUserId: string) {
  return Array.isArray(data.users) && data.users.includes(leftUserId) && data.users.includes(rightUserId);
}

export async function resolveVibeConnection(
  currentUserId: string,
  targetUserId: string,
): Promise<VibeConnectionResolution> {
  console.info('[Tonyte][RN][VibeSynchronizer] checking existing private chat', { currentUserId, targetUserId });
  const expectedChatId = pairChatId(currentUserId, targetUserId);
  const directChatSnap = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.privateChats, expectedChatId)).catch(() => null);
  if (directChatSnap?.exists() && hasBothUsers(directChatSnap.data(), currentUserId, targetUserId)) {
    console.info('[Tonyte][RN][VibeSynchronizer] existing chat found', { chatId: expectedChatId });
    return { status: 'private_chat', chatId: expectedChatId, chatKind: 'vibe' };
  }

  const chatSnap = await getDocs(
    query(
      collection(firestore, FIRESTORE_COLLECTIONS.privateChats),
      where('users', 'array-contains', currentUserId),
    ),
  ).catch(() => null);
  const existingChatDoc = chatSnap?.docs.find(chatDoc => hasBothUsers(chatDoc.data(), currentUserId, targetUserId));
  if (existingChatDoc) {
    console.info('[Tonyte][RN][VibeSynchronizer] existing chat found', { chatId: existingChatDoc.id });
    return { status: 'private_chat', chatId: existingChatDoc.id, chatKind: 'vibe' };
  }

  const requestIds = [`${currentUserId}_${targetUserId}`, `${targetUserId}_${currentUserId}`];
  const requestSnaps = await Promise.all(
    requestIds.map(requestId => getDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRequests, requestId)).catch(() => null)),
  );
  const acceptedRequestIndex = requestSnaps.findIndex(snap => snap?.exists() && snap.data().status === 'accepted');
  if (acceptedRequestIndex >= 0) {
    const requestId = requestIds[acceptedRequestIndex];
    const data = requestSnaps[acceptedRequestIndex]?.data() as { privateChatId?: string } | undefined;
    const requestChatId = data?.privateChatId || '';
    const requestChatSnap = requestChatId
      ? await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.privateChats, requestChatId)).catch(() => null)
      : null;
    const requestChatExists = requestChatSnap?.exists() && hasBothUsers(requestChatSnap.data(), currentUserId, targetUserId);
    console.info('[Tonyte][RN][VibeSynchronizer] existing vibe request found', {
      requestId,
      privateChatId: requestChatExists ? requestChatId : '',
    });
    return {
      status: 'accepted_vibe',
      chatId: requestChatExists ? requestChatId : undefined,
      chatKind: requestChatExists ? 'vibe' : undefined,
      requestId,
    };
  }

  const legacySnap = await getDocs(
    query(
      collection(firestore, FIRESTORE_COLLECTIONS.matches),
      where('users', 'array-contains', currentUserId),
    ),
  ).catch(() => null);
  const legacyDoc = legacySnap?.docs.find(matchDoc => hasBothUsers(matchDoc.data(), currentUserId, targetUserId));
  if (legacyDoc) {
    console.info('[Tonyte][RN][VibeSynchronizer] existing legacy match found', { matchId: legacyDoc.id });
    return { status: 'legacy_match', chatId: legacyDoc.id, chatKind: 'match', legacyMatchId: legacyDoc.id };
  }

  return { status: 'none' };
}

export async function ensurePrivateChatForVibeConnection(
  currentUserId: string,
  targetUserId: string,
  resolution?: VibeConnectionResolution,
) {
  const resolved = resolution ?? await resolveVibeConnection(currentUserId, targetUserId);
  if (resolved.chatId && resolved.chatKind) {
    console.info('[Tonyte][RN][VibeSynchronizer] skipping duplicate chat', {
      chatId: resolved.chatId,
      status: resolved.status,
    });
    return resolved;
  }

  if (resolved.status !== 'accepted_vibe' && resolved.status !== 'none') return resolved;

  const chatId = pairChatId(currentUserId, targetUserId);
  console.info('[Tonyte][RN][VibeSynchronizer] creating new chat', { chatId, currentUserId, targetUserId });
  await setDoc(
    doc(firestore, FIRESTORE_COLLECTIONS.privateChats, chatId),
    {
      users: [currentUserId, targetUserId].sort(),
      createdAt: serverTimestamp(),
      fromDayVibes: true,
      fromVibeRequestId: resolved.requestId || '',
      lastMessage: '',
      lastMessageAt: serverTimestamp(),
      lastMessageSenderId: '',
    },
    { merge: true },
  );
  return { ...resolved, status: 'private_chat' as const, chatId, chatKind: 'vibe' as const };
}

export function buildVibeIcebreakers({
  similarityPercent,
  sharedAnswerTexts,
  sharedTraits,
}: {
  similarityPercent: number;
  sharedAnswerTexts: string[];
  sharedTraits: string[];
}) {
  const signals = [...sharedAnswerTexts, ...sharedTraits].filter(Boolean);
  const first = signals[0] || 'today\'s shared vibe';
  const second = signals[1] || signals[0] || 'your shared frequency';
  const highSync = similarityPercent >= 90;

  const prompts = [
    `You both leaned toward ${first.toLowerCase()} energy. What would you actually say yes to today?`,
    `Your shared frequency includes ${second.toLowerCase()}. What setting would make that feel effortless?`,
  ];

  if (highSync) return prompts;
  return [prompts[0]];
}

export async function injectVibeIcebreakersOnce({
  dateKey,
  userA,
  userB,
  chatId,
  similarityPercent,
  icebreakers,
}: {
  dateKey: string;
  userA: string;
  userB: string;
  chatId: string;
  similarityPercent: number;
  icebreakers: string[];
}) {
  const markerId = pairMarkerId(dateKey, userA, userB);
  const markerRef = doc(firestore, FIRESTORE_COLLECTIONS.vibeIcebreakers, markerId);
  const markerSnap = await getDoc(markerRef).catch(() => null);
  if (markerSnap?.exists()) {
    console.info('[Tonyte][RN][VibeSynchronizer] icebreaker marker already exists', { markerId, chatId });
    return false;
  }

  console.info('[Tonyte][RN][VibeSynchronizer] injecting icebreakers', { markerId, chatId, similarityPercent });
  const activityAt = serverTimestamp();
  await setDoc(markerRef, {
    date: dateKey,
    userIds: [userA, userB].sort(),
    chatId,
    similarityPercent,
    icebreakers,
    injectedAt: serverTimestamp(),
  });
  await addDoc(collection(firestore, FIRESTORE_COLLECTIONS.privateChats, chatId, 'messages'), {
    type: 'vibe_icebreaker',
    senderId: 'system',
    senderName: 'Tonyte',
    text: 'Your vibe sync prompts are ready',
    prompts: icebreakers,
    createdAt: activityAt,
    date: dateKey,
  });
  await setDoc(
    doc(firestore, FIRESTORE_COLLECTIONS.privateChats, chatId),
    {
      lastMessage: 'Vibe sync prompts are ready',
      lastMessageAt: activityAt,
      lastActivityAt: activityAt,
      lastMessageSenderId: 'system',
    },
    { merge: true },
  );
  return true;
}
